/**
 * MapService - Handles all Leaflet map operations
 * Manages map initialization, markers, circles, and view updates
 */
class MapService {
  constructor () {
    this.map = null
    this.mapReady = false
    this.siteMarker = null
    this.userMarker = null
    this.siteCircle = null
    this.siteIcon = null
    this.userIcon = null
    this.fitBoundsTimer = null
  }

  /**
   * Get Leaflet instance from window
   */
  getLeaflet () {
    if (typeof window !== 'undefined' && window.L) {
      return window.L
    }
    return null
  }

  /**
   * Create and initialize the map
   */
  async createMap (container) {
    return new Promise((resolve, reject) => {
      if (!container) {
        reject(new Error('Map container not available'))
        return
      }

      if (this.map) {
        resolve(this.map)
        return
      }

      const L = this.getLeaflet()
      if (!L) {
        setTimeout(() => this.createMap(container).then(resolve).catch(reject), 120)
        return
      }

      this.map = L.map(container, {
        zoomControl: false,
        attributionControl: false
      })

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '© OpenStreetMap contributors'
      }).addTo(this.map)

      this.map.setView([0, 0], 16)
      this.mapReady = true

      this.map.whenReady(() => {
        resolve(this.map)
      })
    })
  }

  /**
   * Ensure marker icons are initialized
   */
  ensureIcons () {
    const L = this.getLeaflet()
    if (!L) {
      return false
    }

    if (!this.siteIcon) {
      this.siteIcon = L.icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-green.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      })
    }

    if (!this.userIcon) {
      this.userIcon = L.icon({
        iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-blue.png',
        shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41]
      })
    }

    return true
  }

  /**
   * Update or create site marker and circle on the map
   */
  updateSiteLayer (site) {
    if (!this.map || !site || site.latitude == null || site.longitude == null) {
      return
    }

    const lat = Number(site.latitude)
    const lng = Number(site.longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return
    }

    const latLng = [lat, lng]
    this.ensureIcons()

    const L = this.getLeaflet()
    if (!L) return

    // Update or create site marker
    if (this.siteMarker) {
      this.siteMarker.setLatLng(latLng)
    } else {
      this.siteMarker = L.marker(latLng, { icon: this.siteIcon }).addTo(this.map)
    }

    // Update or create site circle
    const radiusValue = Number(site.radius)
    const radius = Number.isFinite(radiusValue) && radiusValue > 0 ? radiusValue : 50

    if (this.siteCircle) {
      this.siteCircle.setLatLng(latLng)
      this.siteCircle.setRadius(radius)
    } else {
      this.siteCircle = L.circle(latLng, {
        color: '#42a5f5',
        fillColor: '#42a5f5',
        fillOpacity: 0.18,
        radius: radius
      }).addTo(this.map)
    }

    this.fitToBounds()
  }

  /**
   * Update or create user location marker on the map
   */
  updateUserMarker (userLocation) {
    if (!this.map || !userLocation || userLocation.latitude == null || userLocation.longitude == null) {
      return
    }

    const lat = Number(userLocation.latitude)
    const lng = Number(userLocation.longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return
    }

    const latLng = [lat, lng]
    this.ensureIcons()

    const L = this.getLeaflet()
    if (!L) {
      setTimeout(() => this.updateUserMarker(userLocation), 120)
      return
    }

    if (this.userMarker) {
      this.userMarker.setLatLng(latLng)
    } else {
      this.userMarker = L.marker(latLng, { icon: this.userIcon }).addTo(this.map)
    }

    this.fitToBounds()
  }

  /**
   * Focus the map view on the site circle
   */
  focusOnSite (options = {}) {
    if (!this.map) {
      return
    }

    // If circle exists but hasn't been added to a map yet
    if (this.siteCircle && !this.siteCircle._map) {
      return
    }

    if (!this.siteCircle) {
      return
    }

    this.fitToBounds()
  }

  /**
   * Fit map bounds to show both user and site markers, including site circle
   * Debounced to prevent multiple rapid calls from conflicting
   */
  fitToBounds () {
    const L = this.getLeaflet()
    if (!L || !this.map) return

    // Clear any pending fitBounds call
    if (this.fitBoundsTimer) {
      clearTimeout(this.fitBoundsTimer)
    }

    // Debounce the actual fit operation
    this.fitBoundsTimer = setTimeout(() => {
      const elements = []

      // Add user marker to bounds
      if (this.userMarker) {
        elements.push(this.userMarker.getLatLng())
      }

      // Add site marker to bounds
      if (this.siteMarker) {
        elements.push(this.siteMarker.getLatLng())
      }

      // Add site circle bounds to ensure the entire radius is visible
      if (this.siteCircle && this.siteCircle._map) {
        const circleBounds = this.siteCircle.getBounds()
        elements.push(circleBounds.getNorthEast())
        elements.push(circleBounds.getSouthWest())
      }

      // If we have at least one element, fit bounds to it
      if (elements.length > 0) {
        const bounds = L.latLngBounds(elements)
        const padding = [50, 50]

        this.map.invalidateSize() // Recalculate map size
        this.map.fitBounds(bounds, { padding, animate: true, maxZoom: 17 })
      }
    }, 200)
  }

  /**
   * Clean up map resources
   */
  destroy () {
    if (this.map) {
      this.map.remove()
      this.map = null
    }
    this.siteMarker = null
    this.userMarker = null
    this.siteCircle = null
    this.mapReady = false
  }
}

export default MapService
